/**
 * Dropped-in unit sprite sheets, found and swapped at render time.
 *
 * The same door `ui/artdrop.ts` opens for leader portraits, opened for unit
 * art: if a sheet exists at `art/units/<unitTypeId>.png`, the scene draws it
 * in place of the procedural vector body; a missing file quietly keeps the
 * vector. Nothing here is state, nothing is saved, and turning it off
 * changes no outcome — the renderer-only rule from COORDINATION.md.
 *
 * The sheet contract (baked by the artgen pipeline, but any file matching it
 * works): a grid of square cells, **8 columns of facings** — column k faces
 * `k × 45°` clockwise from screen-east, matching the game's facing angle
 * directly — and one row per walk frame, row 0 topmost. Cell size is
 * `width / 8`; the row count is whatever the image height holds. A sprite
 * body is drawn unrotated (the sheet already turned), so the engine's
 * separate weapon sprite keeps swinging over it exactly as it does over the
 * vector body.
 */
import { Rectangle, Texture } from 'pixi.js';

export interface UnitSheet {
  dirs: number;
  frames: number;
  /** [direction][frame]. */
  cells: Texture[][];
}

const probed = new Map<string, UnitSheet | 'missing' | 'pending'>();

/**
 * The sheet for a unit type, or null while missing or still loading.
 *
 * Probes through `new Image()` for the same CSP reason artdrop does, and
 * caches the answer per id: one probe per unit type, however many thousand
 * times a frame asks.
 */
export function unitSheet(id: string): UnitSheet | null {
  const hit = probed.get(id);
  if (hit !== undefined) return hit === 'missing' || hit === 'pending' ? null : hit;
  probed.set(id, 'pending');
  const img = new Image();
  img.onload = () => {
    const dirs = 8;
    const cell = Math.floor(img.width / dirs);
    if (cell <= 0 || img.height < cell) {
      probed.set(id, 'missing');
      return;
    }
    const frames = Math.max(1, Math.floor(img.height / cell));
    const base = Texture.from(img);
    const cells: Texture[][] = [];
    for (let d = 0; d < dirs; d++) {
      const col: Texture[] = [];
      for (let f = 0; f < frames; f++) {
        col.push(new Texture({ source: base.source, frame: new Rectangle(d * cell, f * cell, cell, cell) }));
      }
      cells.push(col);
    }
    probed.set(id, { dirs, frames, cells });
  };
  img.onerror = () => probed.set(id, 'missing');
  img.src = `art/units/${id}.png`;
  return null;
}

/** Facing angle → sheet column, nearest of the 8 (or n) baked directions. */
export function sheetDir(facing: number, dirs: number): number {
  const tau = Math.PI * 2;
  const a = ((facing % tau) + tau) % tau;
  return Math.round((a / tau) * dirs) % dirs;
}

// ── Buildings ───────────────────────────────────────────────────────────────

const bldgProbed = new Map<string, Texture | 'missing' | 'pending'>();

/**
 * A dropped-in building still at `art/buildings/<vernacular>/<id>.png`, or
 * null.
 *
 * Namespaced by the people whose architecture is showing, because the
 * building catalogue is shared across races but its *drawing* never was —
 * `setArchitecture` rebakes the whole vector bank per vernacular, and a
 * drop-in that ignored that would put a human hearth-hall in a bone-built
 * Litany town. A still only exists for the peoples somebody has baked; the
 * others keep their vector vernacular, per building, quietly.
 *
 * One image, any resolution: the scene stretches it so its width spans the
 * building's footprint, which is what lets these be baked at a finer grain
 * than the vector art's tile pixels. Headroom above the footprint comes from
 * the image simply being taller than wide, exactly as the baked textures do.
 */
export function buildingArt(vernacular: string, id: string): Texture | null {
  const key = `${vernacular}/${id}`;
  const hit = bldgProbed.get(key);
  if (hit !== undefined) return hit === 'missing' || hit === 'pending' ? null : hit;
  bldgProbed.set(key, 'pending');
  const img = new Image();
  img.onload = () => bldgProbed.set(key, Texture.from(img));
  img.onerror = () => bldgProbed.set(key, 'missing');
  img.src = `art/buildings/${key}.png`;
  return null;
}

// ── Props ───────────────────────────────────────────────────────────────────

const propProbed = new Map<string, Texture | 'missing' | 'pending'>();

/**
 * A dropped-in prop still at `art/props/<id>.png`, or null.
 *
 * The third door, and the simplest: one image, no facings, no frames. A tree
 * is the same tree from every angle in a dimetric projection, which is the
 * whole reason the resource props and the battlefield scatter are stills and
 * the villagers and the mounts are sheets.
 *
 * Not namespaced by vernacular, unlike `buildingArt`. A people builds its own
 * houses; it does not grow its own boulders, and a Litany rock is a rock.
 *
 * The caller owns the scale. These are authored square with the ground contact
 * point some way up from the bottom edge (`groundY` in the generator), so the
 * scene anchors them there and stretches them to whatever the simulation says
 * the thing's radius is — one asset serves a boulder of one and a half tiles
 * and one of nearly four, which is what stops forty pieces of scatter being
 * forty copies of the same picture at the same size.
 */
export function propTexture(id: string): Texture | null {
  const hit = propProbed.get(id);
  if (hit !== undefined) return hit === 'missing' || hit === 'pending' ? null : hit;
  propProbed.set(id, 'pending');
  const img = new Image();
  img.onload = () => propProbed.set(id, Texture.from(img));
  img.onerror = () => propProbed.set(id, 'missing');
  img.src = `art/props/${id}.png`;
  return null;
}
